import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Inventory2Outlined, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { LockedFeature } from '../planLock/LockedFeature';
import { usePlanSurface } from '../planLock/usePlanSurface';
import { useToast } from '../../contexts/ToastContext';
import { useUnsavedChangesSource } from '../appUpdate/unsavedChanges';
import { EmptyState, InlineMessage, LoadingState, NumberField, SaveBar, SectionCard, StatusChip, parseNumberInput } from '../ui';
import { reorderLocationPicker } from './reorderLocationPicker';
import {
  REORDER_PAGE_SIZE,
  REORDER_SUGGESTIONS_QUERY_KEY,
  isReorderPlanLocked,
  reorderApi,
  type ReorderStatus,
  type ReorderSuggestion,
  type ReorderSuggestionsResult,
} from './reorderApi';
import { TrialFeatureTitle } from '../trial/TrialFeatureBadge';

type Segment = 'all' | ReorderStatus;

const SEGMENTS: Segment[] = ['all', 'reorder_now', 'watch', 'no_count', 'ok'];

const statusTone = (status: ReorderStatus): 'problem' | 'caution' | 'positive' | 'neutral' =>
  status === 'reorder_now' ? 'problem' : status === 'watch' ? 'caution' : status === 'ok' ? 'positive' : 'neutral';

const fmtDays = (days: number | null): string => (days == null ? '—' : days < 10 ? days.toFixed(1).replace(/\.0$/, '') : Math.round(days).toLocaleString());
const fmtRate = (perDay: number): string => (perDay >= 1 ? perDay.toFixed(1).replace(/\.0$/, '') : (perDay * 7).toFixed(1).replace(/\.0$/, ''));

/**
 * Reorder suggestions table on the Sales Report (flag `predictive_reorder`,
 * plan `advancedAnalytics`; MOT-149): one table for every synced product that
 * sold in the window or was counted, filterable by status, with the on-hand
 * count edited in the row and saved from one dirty-only save bar. The inputs
 * behind each number (rate, lead time, safety days, count date) are in the
 * row, so the suggestion is never a black box. The ranked list is read a page
 * at a time ("Show more"); the status counts always cover the whole list.
 * With the Shopify stock switch on (MOT-395) the on-hand cell says whether the
 * number is the store's count or a Shopify read, and a line under the
 * coverage says when Shopify was read, or that the store must reconnect.
 * A store with several locations picks one (MOT-396): that location's sales,
 * counts and Shopify stock; a member assigned to locations only sees theirs.
 * The picker is locked while counts are unsaved, so a count is never saved
 * against the wrong location.
 */
export const ReorderSuggestionsSection: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { enabled } = useOrgFeatureFlag('predictive_reorder');
  const surface = usePlanSurface({ feature: 'advancedAnalytics', orgFlag: 'predictive_reorder' });
  const [segment, setSegment] = useState<Segment>('all');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /** '' = the whole store (or, for a location-scoped member, their first location). */
  const [locationId, setLocationId] = useState('');

  const query = useInfiniteQuery({
    queryKey: [...REORDER_SUGGESTIONS_QUERY_KEY, 'table', locationId],
    queryFn: ({ pageParam }) =>
      reorderApi.listSuggestions(t('store.reorder.loadError'), { limit: REORDER_PAGE_SIZE, offset: pageParam, locationId: locationId || null }),
    initialPageParam: 0,
    getNextPageParam: (last: ReorderSuggestionsResult) => last.nextOffset ?? undefined,
    enabled: enabled && surface.mode === 'open',
    retry: false,
  });

  const planLocked = query.isError && isReorderPlanLocked(query.error);
  const pages = query.data?.pages ?? [];
  // Totals, counts and settings come from the latest page (they cover the whole list on every page).
  const data = pages.length ? pages[pages.length - 1] : undefined;
  const loaded = useMemo(() => pages.flatMap((page) => page.suggestions), [pages]);

  const rows = useMemo(() => (segment === 'all' ? loaded : loaded.filter((s) => s.status === segment)), [loaded, segment]);

  const dirtyIds = useMemo(
    () =>
      Object.keys(drafts).filter((id) => {
        const stored = loaded.find((s) => s.productId === id)?.countedOnHand ?? null;
        const typed = parseNumberInput(drafts[id]);
        return typed !== stored;
      }),
    [drafts, loaded],
  );
  const dirty = dirtyIds.length > 0;

  const save = useMutation({
    mutationFn: async () =>
      reorderApi.saveCounts(
        dirtyIds.map((productId) => ({ productId, onHand: parseNumberInput(drafts[productId]) })),
        t('store.reorder.saveCountsError'),
        { locationId: data?.location?.locationId ?? null },
      ),
    onSuccess: (result) => {
      setDrafts({});
      showToast(t('store.reorder.countsSaved', { count: result.written + result.cleared }), 'success');
      queryClient.invalidateQueries({ queryKey: REORDER_SUGGESTIONS_QUERY_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const discard = () => setDrafts({});
  useUnsavedChangesSource({ dirty, save: () => save.mutateAsync(), discard });

  // Scroll into view when the home card deep-links to #reorder.
  useEffect(() => {
    if (enabled && typeof window !== 'undefined' && window.location.hash === '#reorder') {
      document.getElementById('reorder')?.scrollIntoView({ block: 'start' });
    }
  }, [enabled, query.isSuccess]);

  if (surface.mode === 'locked') {
    return (
      <Box id="reorder" sx={{ scrollMarginTop: 16, mb: 3 }}>
        <LockedFeature variant="inline" planName={surface.planName} title={t('store.reorder.title')} />
      </Box>
    );
  }
  if (!enabled || surface.mode !== 'open') return null;

  const counts = data?.counts;
  const segmentCount = (s: Segment): number => {
    if (!counts) return 0;
    if (s === 'all') return data?.total ?? 0;
    if (s === 'reorder_now') return counts.reorderNow;
    if (s === 'watch') return counts.watch;
    if (s === 'no_count') return counts.noCount;
    return counts.ok;
  };

  const coverage = data
    ? data.syncAll
      ? t('store.reorder.coverageSyncAll', { listed: data.total, days: data.window.days })
      : t('store.reorder.coverage', { listed: data.total, selection: data.selectionSize ?? 0, days: data.window.days })
    : '';
  // Rows of the current filter not loaded yet: the list is paged, the counts are not.
  const unloaded = data ? Math.max(0, segmentCount(segment) - rows.length) : 0;

  const locations = data?.locations ?? [];
  // A location-scoped member has no whole-store view, whatever is picked now.
  const picker = reorderLocationPicker(data, locationId);
  const pickerValue = picker.value;
  const showPicker = picker.show;

  const draftFor = (s: ReorderSuggestion): string =>
    s.productId in drafts ? drafts[s.productId] : s.countedOnHand == null ? '' : String(s.countedOnHand);

  return (
    <Box id="reorder" sx={{ scrollMarginTop: 16, mb: 3 }}>
      <SectionCard
        title={<TrialFeatureTitle title={t('store.reorder.title')} feature="advancedAnalytics" />}
        subtitle={t('store.reorder.intro')}
        leading={<Inventory2Outlined color="primary" />}
        actions={
          <Button size="small" onClick={() => navigate('/app/store-settings')} sx={{ textTransform: 'none' }}>
            {t('store.reorder.openSettings')}
          </Button>
        }
      >
        {query.isLoading ? (
          <LoadingState />
        ) : planLocked ? (
          <Alert severity="info" icon={<Lock fontSize="small" />} action={<Button size="small" onClick={() => navigate('/app/billing')} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>{t('store.reorder.lockedCta')}</Button>} sx={{ borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('store.reorder.lockedTitle')}</Typography>
            <Typography variant="body2">{t('store.reorder.lockedBody')}</Typography>
          </Alert>
        ) : query.isError || !data ? (
          <Stack spacing={1} alignItems="flex-start">
            <InlineMessage severity="error">{(query.error as Error | null)?.message || t('store.reorder.loadError')}</InlineMessage>
            <Button size="small" onClick={() => query.refetch()}>{t('store.reorder.retry')}</Button>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {showPicker && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <TextField
                  select
                  size="small"
                  label={t('store.reorder.location.label')}
                  value={pickerValue}
                  onChange={(e) => setLocationId(e.target.value)}
                  disabled={dirty}
                  helperText={dirty ? t('store.reorder.location.saveFirst') : undefined}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 220 }}
                >
                  {picker.showAll && <MenuItem value="">{t('store.reorder.location.all')}</MenuItem>}
                  {locations.map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.isPrimary ? t('store.reorder.location.primary', { name: l.name }) : l.name}
                    </MenuItem>
                  ))}
                </TextField>
                {data.location?.isPrimary && data.location.unlocatedLines > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('store.reorder.location.unlocated', { count: data.location.unlocatedLines })}
                  </Typography>
                )}
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary">
              {coverage}
            </Typography>
            {data.platformOnHand && data.platformOnHand.status === 'reconnect_required' ? (
              <Alert
                severity="warning"
                sx={{ borderRadius: 2 }}
                action={
                  <Button size="small" color="inherit" onClick={() => navigate('/app/integrations')} sx={{ textTransform: 'none' }}>
                    {t('store.reorder.shopify.reconnectCta')}
                  </Button>
                }
              >
                {t('store.reorder.shopify.reconnect')}
              </Alert>
            ) : data.platformOnHand && data.platformOnHand.status === 'no_integration' ? (
              <InlineMessage severity="info">{t('store.reorder.shopify.noIntegration')}</InlineMessage>
            ) : data.platformOnHand ? (
              <Typography variant="caption" color={data.platformOnHand.status === 'error' ? 'error' : 'text.secondary'}>
                {data.platformOnHand.status === 'error'
                  ? t('store.reorder.shopify.error')
                  : data.platformOnHand.fetchedAt
                    ? t(data.platformOnHand.truncated ? 'store.reorder.shopify.readTruncated' : 'store.reorder.shopify.read', {
                        count: data.platformOnHand.products,
                        time: new Date(data.platformOnHand.fetchedAt).toLocaleString(),
                      })
                    : t('store.reorder.shopify.notRead')}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {SEGMENTS.map((s) => {
                const n = segmentCount(s);
                const attention = s === 'reorder_now' && n > 0;
                return (
                  <Chip
                    key={s}
                    label={`${t(`store.reorder.segments.${s}`)} · ${n.toLocaleString()}`}
                    size="small"
                    color={segment === s ? 'primary' : attention ? 'error' : 'default'}
                    variant={segment === s ? 'filled' : 'outlined'}
                    onClick={() => setSegment(s)}
                  />
                );
              })}
            </Stack>

            {rows.length === 0 ? (
              <EmptyState
                title={t(`store.reorder.empty.${segment}.title`)}
                description={
                  segment === 'all' && data.selectionSize === 0
                    ? t('store.reorder.empty.noSelection')
                    : t(`store.reorder.empty.${segment}.body`, { days: data.window.days })
                }
                action={
                  unloaded > 0 && query.hasNextPage ? (
                    <Button size="small" variant="outlined" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                      {t('store.reorder.showMore', { count: unloaded })}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('store.reorder.columns.product')}</TableCell>
                      <TableCell>{t('store.reorder.columns.status')}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('store.reorder.columns.rateHint', { days: data.window.days, recent: data.window.recentDays })}>
                          <span>{t('store.reorder.columns.rate')}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{t('store.reorder.columns.onHand')}</TableCell>
                      <TableCell align="right">{t('store.reorder.columns.daysOfSupply')}</TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('store.reorder.columns.reorderPointHint')}>
                          <span>{t('store.reorder.columns.reorderPoint')}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{t('store.reorder.columns.suggestedQty')}</TableCell>
                      <TableCell align="right">{t('store.reorder.columns.count')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((s) => {
                      const rateLabel = s.velocityPerDay >= 1 ? t('store.reorder.perDay', { n: fmtRate(s.velocityPerDay) }) : t('store.reorder.perWeek', { n: fmtRate(s.velocityPerDay) });
                      const countedNote =
                        s.onHandSource === 'shopify' && s.platformOnHandAt
                          ? t('store.reorder.fromShopify', { time: new Date(s.platformOnHandAt).toLocaleString(), sold: s.soldSinceCount.toLocaleString() })
                          : s.countedAt == null
                            ? t('store.reorder.notCounted')
                            : t('store.reorder.countedOn', { date: new Date(s.countedAt).toLocaleDateString(), sold: s.soldSinceCount.toLocaleString() });
                      return (
                        <TableRow key={s.productId} hover sx={{ '& td': { verticalAlign: 'top' } }}>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[s.brand, s.sku].filter(Boolean).join(' · ') || s.productId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip tone={statusTone(s.status)} label={t(`store.reorder.status.${s.status}`)} />
                            {s.countStale && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t('store.reorder.countStale', { count: (s.countedOnHand ?? 0).toLocaleString() })}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {rateLabel}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('store.reorder.unitsInWindow', { units: s.unitsSoldWindow.toLocaleString(), recent: s.unitsSoldRecent.toLocaleString() })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {s.onHand == null ? '—' : s.onHand.toLocaleString()}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {countedNote}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDays(s.daysOfSupply)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {s.reorderPoint.toLocaleString()}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('store.reorder.leadLine', { lead: s.leadTimeDays, safety: s.safetyStockDays })}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: s.suggestedQty ? 700 : 400 }}>
                            {s.suggestedQty == null ? '—' : s.suggestedQty.toLocaleString()}
                          </TableCell>
                          <TableCell align="right" sx={{ width: 120 }}>
                            <NumberField
                              value={draftFor(s)}
                              onChange={(value) => setDrafts((d) => ({ ...d, [s.productId]: value }))}
                              min={0}
                              integer
                              size="small"
                              aria-label={t('store.reorder.columns.count')}
                              placeholder="—"
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {rows.length > 0 && query.hasNextPage && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                  {t('store.reorder.showMore', { count: unloaded })}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {t('store.reorder.loadedOf', { loaded: loaded.length, total: data.total })}
                </Typography>
              </Stack>
            )}
            <SaveBar dirty={dirty} saving={save.isPending} message={t('store.reorder.saveBar', { count: dirtyIds.length })} onSave={() => save.mutate()} onDiscard={discard} />
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
};

export default ReorderSuggestionsSection;
